// 🎮 src/components/admin/AdminPanel.jsx - In-Game Free Code Management (Enhanced)
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../auth/auth-components';
import { logger } from '../../utils/logger';

const hasWindow = typeof window !== 'undefined';
const hasClipboard = typeof navigator !== 'undefined' && !!navigator.clipboard;

const toDateSafe = (v) => {
  if (!v) return null;
  try {
    if (typeof v?.toDate === 'function') return v.toDate();
    if (v?.seconds) return new Date(v.seconds * 1000);
    if (v instanceof Date) return v;
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

const AdminPanel = () => {
  const { user } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  const [newCodeForm, setNewCodeForm] = useState({
    fanType: 'diehard', // 🎁 DEFAULT TO DIEHARD for free users
    uses: 1,
    expiresInDays: 30,
    description: 'Free DieHard access',
    email: ''
  });

  const [showForm, setShowForm] = useState(false);
  const [lastGenerated, setLastGenerated] = useState(null);

  // Admin allowlist (keep as constants or pull from env/config)
  const ADMIN_EMAILS = useMemo(
    () => ['bobby.40bags@gmail.com', 'your-admin-email@gmail.com'],
    []
  );

  // Check if user is admin
  const isAdmin = useMemo(
    () => !!user && !!user.email && ADMIN_EMAILS.includes(user.email),
    [user, ADMIN_EMAILS]
  );

  // Generate promotional message
  const generatePromoMessage = useCallback((code, smartLink) => {
    return `🏈 Check out First Snap for FREE! 
Get instant access to our premium football analysis game. 
Click here: ${smartLink}

✅ No payment required
✅ Full game experience  
✅ Premium team analysis

Use Code: ${code}`;
  }, []);

  const ensureService = useCallback(() => {
    if (!hasWindow || !window.freeCodeService) {
      const msg = 'freeCodeService is not available in window.';
      logger.error(msg);
      throw new Error(msg);
    }
    return window.freeCodeService;
  }, []);

  // Load codes and stats
  const loadData = useCallback(async () => {
    setError(null);
    try {
      setLoading(true);
      const svc = ensureService();

      const [codesResult, statsResult] = await Promise.all([
        svc.listFreeCodes(),
        svc.getFreeCodeStats()
      ]);

      setCodes(Array.isArray(codesResult) ? codesResult : []);
      setStats(statsResult || null);
      logger.success('✅ Admin data loaded');
    } catch (err) {
      logger.error('Error loading admin data:', err);
      setError(err?.message || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, [ensureService]);

  // Generate new free code
  const handleGenerateCode = useCallback(async () => {
    try {
      setLoading(true);
      const svc = ensureService();

      const config = {
        fanType: newCodeForm.fanType,
        uses: parseInt(newCodeForm.uses, 10) || 1,
        expiresInDays: parseInt(newCodeForm.expiresInDays, 10) || 30,
        description: newCodeForm.description || 'Free DieHard access',
        email: newCodeForm.email?.trim() || null
      };

      const result = await svc.generateFreeCode(config);

      if (result?.success) {
        setLastGenerated(result);
        setShowForm(false);
        await loadData(); // Refresh the list

        // Generate promotional message
        const promoMessage = generatePromoMessage(result.code, result.smartLink);

        // Copy promotional message to clipboard
        if (hasClipboard) {
          try {
            await navigator.clipboard.writeText(promoMessage);
            alert(
              `✅ Free code generated and promotional message copied to clipboard!\n\nCode: ${result.code}\n\nReady to share:\n${promoMessage}`
            );
          } catch (clipError) {
            logger.warn('Clipboard write failed, showing details in alert.');
            alert(
              `✅ Free code generated!\n\nCode: ${result.code}\nLink: ${result.smartLink}\n\nPromo Message:\n${promoMessage}`
            );
          }
        } else {
          alert(
            `✅ Free code generated!\n\nCode: ${result.code}\nLink: ${result.smartLink}\n\nPromo Message:\n${promoMessage}`
          );
        }
      } else {
        throw new Error(result?.message || 'Failed to generate code');
      }
    } catch (error) {
      logger.error('Error generating free code:', error);
      alert('❌ Error generating free code: ' + (error?.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [ensureService, generatePromoMessage, loadData, newCodeForm]);

  // Deactivate a code
  const handleDeactivateCode = useCallback(
    async (code) => {
      // SSR-safe confirm
      const ok = !hasWindow || window.confirm(`Are you sure you want to deactivate code ${code}?`);
      if (!ok) return;

      try {
        setLoading(true);
        const svc = ensureService();
        await svc.deactivateFreeCode(code);
        await loadData(); // Refresh the list
        alert(`✅ Code ${code} deactivated successfully`);
      } catch (error) {
        logger.error('Error deactivating code:', error);
        alert('❌ Error deactivating code: ' + (error?.message || 'Unknown error'));
      } finally {
        setLoading(false);
      }
    },
    [ensureService, loadData]
  );

  // Load data when panel opens
  useEffect(() => {
    if (isOpen && isAdmin) {
      loadData();
    }
  }, [isOpen, isAdmin, loadData]);

  // Don't show for non-admin users
  if (!isAdmin) return null;

  return (
    <>
      {/* Admin Panel Toggle Button */}
      <button
        onClick={() => setIsOpen((v) => !v)}
        style={{
          position: 'fixed',
          bottom: '20px',
          left: '20px',
          zIndex: 9999,
          backgroundColor: '#7c3aed',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '60px',
          height: '60px',
          fontSize: '24px',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        title="Admin Panel"
        aria-label="Open Free Code Admin Panel"
      >
        🎁
      </button>

      {/* Admin Panel Modal */}
      {isOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: 0,
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)'
            }}
          >
            {/* Header */}
            <div
              style={{
                background: 'linear-gradient(135deg, #7c3aed, #5b21b6)',
                color: 'white',
                padding: '24px',
                borderRadius: '12px 12px 0 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div>
                <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '700' }}>
                  🎁 Free Code Admin Panel
                </h2>
                <p style={{ margin: 0, opacity: 0.9, fontSize: '14px' }}>
                  Generate and manage free access codes
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '20px'
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: '24px' }}>
              {error && (
                <div
                  style={{
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    color: '#dc2626',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '16px'
                  }}
                >
                  ⚠️ {error}
                </div>
              )}

              {loading && (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
                  <div>Loading...</div>
                </div>
              )}

              {/* Statistics */}
              {stats && (
                <div
                  style={{
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '24px'
                  }}
                >
                  <h3
                    style={{
                      margin: '0 0 12px 0',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#000000'
                    }}
                  >
                    📊 Statistics
                  </h3>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                      gap: '12px'
                    }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#059669' }}>
                        {stats.totalCodes ?? 0}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Total Codes</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#7c3aed' }}>
                        {stats.activeCodes ?? 0}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Active</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#dc2626' }}>
                        {stats.expiredCodes ?? 0}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Expired</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#f59e0b' }}>
                        {stats.totalUses ?? 0}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Total Uses</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Generate New Code Button */}
              <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                <button
                  onClick={() => setShowForm((v) => !v)}
                  disabled={loading}
                  style={{
                    backgroundColor: '#059669',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {showForm ? '❌ Cancel' : '🎁 Generate New Code'}
                </button>
              </div>

              {/* Generate Code Form */}
              {showForm && (
                <div
                  style={{
                    backgroundColor: '#f0fdf4',
                    border: '2px solid #10b981',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '24px'
                  }}
                >
                  <h3
                    style={{
                      margin: '0 0 16px 0',
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#065f46'
                    }}
                  >
                    Create New Free Code
                  </h3>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '16px',
                      marginBottom: '16px'
                    }}
                  >
                    {/* Fan Type */}
                    <div>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: '4px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#374151'
                        }}
                      >
                        Fan Type
                      </label>
                      <select
                        value={newCodeForm.fanType}
                        onChange={(e) =>
                          setNewCodeForm((prev) => ({ ...prev, fanType: e.target.value }))
                        }
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '2px solid #d1fae5',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="dedicated">Dedicated ($0.99)</option>
                        <option value="expert">Expert ($1.99)</option>
                        <option value="diehard">DieHard ($1.99)</option>
                      </select>
                    </div>

                    {/* Uses */}
                    <div>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: '4px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#374151'
                        }}
                      >
                        Number of Uses
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="1000"
                        value={newCodeForm.uses}
                        onChange={(e) =>
                          setNewCodeForm((prev) => ({ ...prev, uses: e.target.value }))
                        }
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '2px solid #d1fae5',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                    </div>

                    {/* Expires */}
                    <div>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: '4px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#374151'
                        }}
                      >
                        Expires in Days
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={newCodeForm.expiresInDays}
                        onChange={(e) =>
                          setNewCodeForm((prev) => ({
                            ...prev,
                            expiresInDays: e.target.value
                          }))
                        }
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '2px solid #d1fae5',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div style={{ marginBottom: '16px' }}>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: '4px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#374151'
                      }}
                    >
                      Description
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., Beta tester access, Launch promotion..."
                      value={newCodeForm.description}
                      onChange={(e) =>
                        setNewCodeForm((prev) => ({ ...prev, description: e.target.value }))
                      }
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '2px solid #d1fae5',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  {/* Email (Optional) */}
                  <div style={{ marginBottom: '16px' }}>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: '4px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#374151'
                      }}
                    >
                      Restrict to Email (Optional)
                    </label>
                    <input
                      type="email"
                      placeholder="Leave empty for anyone to use"
                      value={newCodeForm.email}
                      onChange={(e) =>
                        setNewCodeForm((prev) => ({ ...prev, email: e.target.value }))
                      }
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '2px solid #d1fae5',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <button
                    onClick={handleGenerateCode}
                    disabled={loading}
                    style={{
                      width: '100%',
                      backgroundColor: loading ? '#9ca3af' : '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? '⏳ Generating...' : '🎁 Generate Free Code'}
                  </button>
                </div>
              )}

              {/* Last Generated Code */}
              {lastGenerated && (
                <div
                  style={{
                    backgroundColor: '#fef3c7',
                    border: '2px solid #f59e0b',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '24px'
                  }}
                >
                  <h3
                    style={{
                      margin: '0 0 12px 0',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#92400e'
                    }}
                  >
                    🎉 Last Generated Code
                  </h3>
                  <div style={{ fontSize: '14px', color: '#92400e', marginBottom: '12px' }}>
                    <div>
                      <strong>Code:</strong> {lastGenerated.code}
                    </div>
                    <div>
                      <strong>Smart Link:</strong>
                      {' '}
                      <a
                        href={lastGenerated.smartLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#dc2626', textDecoration: 'none', marginLeft: '4px' }}
                      >
                        {lastGenerated.smartLink}
                      </a>
                    </div>
                  </div>

                  {/* Promotional Message */}
                  <div
                    style={{
                      backgroundColor: '#fff7ed',
                      border: '1px solid #fed7aa',
                      borderRadius: '8px',
                      padding: '12px',
                      marginTop: '12px'
                    }}
                  >
                    <div
                      style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#9a3412',
                        marginBottom: '8px'
                      }}
                    >
                      📱 Ready-to-Share Message:
                    </div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#9a3412',
                        fontFamily: 'monospace',
                        backgroundColor: 'white',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #fed7aa',
                        whiteSpace: 'pre-line'
                      }}
                    >
                      {generatePromoMessage(lastGenerated.code, lastGenerated.smartLink)}
                    </div>
                    <button
                      onClick={() => {
                        const promoMessage = generatePromoMessage(
                          lastGenerated.code,
                          lastGenerated.smartLink
                        );
                        if (!hasClipboard) {
                          alert(promoMessage);
                          return;
                        }
                        navigator.clipboard.writeText(promoMessage).then(
                          () => alert('📱 Promotional message copied to clipboard!'),
                          () => alert(promoMessage)
                        );
                      }}
                      style={{
                        backgroundColor: '#ea580c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '6px 12px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        marginTop: '8px'
                      }}
                    >
                      📋 Copy Message
                    </button>
                  </div>
                </div>
              )}

              {/* Codes List */}
              {codes.length > 0 && (
                <div>
                  <h3
                    style={{
                      margin: '0 0 16px 0',
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#000000'
                    }}
                  >
                    📋 All Free Codes ({codes.length})
                  </h3>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {codes.map((code) => {
                      const exp = toDateSafe(code.expiresAt);
                      const expLabel = exp ? exp.toLocaleDateString() : 'Never';
                      const smartLink =
                        (hasWindow ? window.location.origin : '') + `/?free=${code.code}`;

                      return (
                        <div
                          key={code.code}
                          style={{
                            backgroundColor: code.isActive ? '#f8fafc' : '#fef2f2',
                            border: `1px solid ${code.isActive ? '#e2e8f0' : '#fecaca'}`,
                            borderRadius: '8px',
                            padding: '12px',
                            marginBottom: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontWeight: '600',
                                fontSize: '16px',
                                marginBottom: '4px',
                                color: '#000000',
                                wordBreak: 'break-all'
                              }}
                            >
                              {code.code} {!code.isActive && '(Deactivated)'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                              {code.fanType} • {code.usedCount ?? 0}/{code.totalUses ?? 0} uses •
                              {' '}Expires: {expLabel}
                            </div>
                            {code.description && (
                              <div style={{ fontSize: '12px', color: '#059669', marginTop: '2px' }}>
                                {code.description}
                              </div>
                            )}
                            {code.email && (
                              <div style={{ fontSize: '12px', color: '#7c3aed', marginTop: '2px' }}>
                                Restricted to: {code.email}
                              </div>
                            )}
                          </div>

                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button
                              onClick={() => {
                                const promoMessage = generatePromoMessage(code.code, smartLink);
                                if (!hasClipboard) {
                                  alert(promoMessage);
                                  return;
                                }
                                navigator.clipboard
                                  .writeText(promoMessage)
                                  .then(() => alert('📱 Promotional message copied to clipboard!'));
                              }}
                              style={{
                                backgroundColor: '#059669',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                            >
                              📱 Copy Promo
                            </button>

                            <button
                              onClick={() => {
                                if (!hasClipboard) {
                                  alert(smartLink);
                                  return;
                                }
                                navigator.clipboard
                                  .writeText(smartLink)
                                  .then(() => alert('Smart link copied to clipboard!'));
                              }}
                              style={{
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                            >
                              📋 Copy Link
                            </button>

                            {code.isActive && (
                              <button
                                onClick={() => handleDeactivateCode(code.code)}
                                style={{
                                  backgroundColor: '#dc2626',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '4px 8px',
                                  fontSize: '12px',
                                  cursor: 'pointer'
                                }}
                              >
                                🗑️ Deactivate
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Refresh Button */}
              <div style={{ textAlign: 'center', marginTop: '24px' }}>
                <button
                  onClick={loadData}
                  disabled={loading}
                  style={{
                    backgroundColor: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                >
                  🔄 Refresh Data
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminPanel;